import { createOpenBankingPayment, getOpenBankingPayment } from "@api";
import { Logo, SecondaryButton, SummaryRow } from "@components";
import { formatAmount, formatSortCode, redirectToThirdParty } from "@utils";
import { useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { MerchantButton, ProcessingIcon } from "../../components";

const ConfirmBankPayment = ({
  institution,
  merchant,
  paymentParameters,
  returnUrl,
  failedUrl,
  successUrl,
  processingUrl,
  onProcessing,
  onSuccess,
  onFailed,
  onBack,
  flowType = "redirect",
}) => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [payment, setPayment] = useState(null);

  const doRedirect = () => {
    if (payment) {
      redirectToThirdParty(payment.paymentUrl);
    } else {
      setTimeout(doRedirect, 100);
    }
  };

  const onPaymentConfirmed = async () => {
    if (flowType == "redirect") {
      setRedirecting(true); // Show the redirect screen immediately
    } else if (flowType == "poll") {
      setPolling(true);
    }

    const callback = `${window.location.origin}/yapilycallback?p-id=`;

    // Start a timer immediately to ensure a minimum wait of 1 second
    const redirectAfter = Date.now() + 1000;

    const response = await createOpenBankingPayment(
      merchant.id,
      paymentParameters.paymentRequestId,
      paymentParameters.customerId,
      paymentParameters.orderNumber,
      paymentParameters.checkoutId,
      paymentParameters.customerName,
      paymentParameters.amount,
      paymentParameters.reference,
      institution.id,
      callback,
      paymentParameters.source,
      paymentParameters.branchId,
      paymentParameters.customerEmail,
      paymentParameters.customerIsFinanced,
      paymentParameters.transactionIds,
      paymentParameters.salesOrderIds,
      paymentParameters.paymentDiscounts
    );

    if (response.data) {
      setPayment(response.data);

      if (flowType === "redirect") {
        handleRedirect(response, redirectAfter);
      }
      if (flowType == "poll") {
        handlePoll(response);
      }
    } else {
      const delay = Math.max(0, redirectAfter - Date.now());

      setTimeout(() => {
        navigate(returnUrl + "?error=bank-error");
      }, delay);
    }
  };

  const handleRedirect = (response, redirectAfter) => {
    const delay = Math.max(0, redirectAfter - Date.now());

    setTimeout(() => {
      redirectToThirdParty(response.data.paymentUrl);
    }, delay);
  };

  const handlePoll = async (response) => {
    setTimeout(pollStatus, 10000, response.data.id);
  };

  const pollStatus = async (id) => {
    let paymentData = await getOpenBankingPayment(id);

    switch (paymentData.status) {
      case "Processing":
        if (onProcessing) {
          onProcessing(paymentData);
        }
        if (processingUrl) {
          navigate(`${processingUrl}&p-id=${paymentData.id}&t=openbanking`);
        }
        break;
      case "Initialized":
        setTimeout(pollStatus, 1000, id);
        break;
      case "Cancelled":
      case "PaymentFailed":
        if (onFailed) {
          onFailed(paymentData);
        }
        if (failedUrl) {
          navigate(failedUrl);
        }
        break;
      case "PaymentSucceeded":
        if (onSuccess) {
          onSuccess(paymentData);
        }
        if (successUrl) {
          navigate(`${successUrl}&p-id=${paymentData.id}&t=openbanking`);
        }
        break;
      default:
        console.error("Invalid payment status");
        break;
    }
  };

  return (
    <div
      className={`lg:bg-zinc-100 h-full lg:h-auto lg:pt-12 grow flex flex-col items-center`}
    >
      {!redirecting && !polling && (
        <div className="bg-white w-full lg:max-w-md lg:mx-auto lg:rounded-lg overflow-hidden lg:border border-zinc-300 lg:shadow-sm lg:mb-12 flex flex-col h-full lg:h-auto">
          <div className="flex-1 overflow-y-auto lg:overflow-visible flex flex-col items-center">
            <div className="py-8 border-b border-zinc-200 text-center w-full">
              <h2 className="font-semibold mb-1">Payment amount</h2>
              <h1 className="font-medium text-4xl">
                {formatAmount(paymentParameters.amount)}
              </h1>
            </div>
            <div className="p-5 max-w-md w-full">
              {/* <h3 className="text-lg font-semibold">Confirm payment</h3> */}
              {/* <SummaryRow
                label="Amount"
                value={formatAmount(paymentParameters.amount)}
              /> */}
              <SummaryRow label="Merchant" value={merchant.name} />
              <SummaryRow label="Account no." value={merchant.accountNumber} />
              <SummaryRow
                label="Sort Code"
                value={formatSortCode(merchant.sortCode)}
              />
              <SummaryRow
                isLast
                label="Reference"
                value={paymentParameters.reference}
              />
            </div>
          </div>
          <div className="px-5 py-5 pb-8 sticky bottom-0 lg:static bg-white border-t border-zinc-200 lg:border-t-0 lg:mt-auto">
            <div className="max-w-md mx-auto flex flex-col gap-2">
              <span className="text-sm text-zinc-500 mb-2 text-center">
                You'll be transferred to your banking app to authorise the
                payment via our partner Yapily.
              </span>
              <MerchantButton
                merchant={merchant}
                onClick={onPaymentConfirmed}
                className="flex items-center justify-center gap-2 px-4"
              >
                Authorise with {institution.name}
              </MerchantButton>
            </div>
          </div>
        </div>
      )}
      {redirecting && (
        <div className="pt-16 px-12">
          <div className="h-[146px] flex items-center w-full justify-center gap-2">
            <Logo
              merchant={merchant}
              type="icon"
              className="rounded-lg"
              height="80px"
            />
            <div className="size-20 min-h-20 flex items-center justify-center">
              <ThreeDots width={40} height={40} color="#A1A1AA" />
            </div>
            <img
              className="size-20 rounded-lg"
              src={institution.iconUrl}
              alt={institution.name}
            />
          </div>
          <h1 className="text-xl text-center font-semibold mb-1.5">
            Securely transferring you to {institution.name} for authentication
          </h1>
          <p className="text-zinc-500 text-center">
            Hold tight, we’re contacting your bank to securely initiate your
            payment.
          </p>
        </div>
      )}
      {polling && payment && (
        <div className="pt-16 px-12 flex flex-col items-center">
          <p className="font-semibold mb-7">Scan to pay using mobile banking</p>
          <img className="w-1/2" src={payment.qrUrl} alt="paymentUrl" />
          <p className="font-semibold my-7">Or</p>
          <a
            href={payment.paymentUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="underline text-primary"
          >
            Continue in browser
          </a>
        </div>
      )}
      {polling && !payment && (
        <div className="w-full mt-64 flex items-center justify-center">
          <ProcessingIcon className="m-auto" merchant={merchant} />
        </div>
      )}
    </div>
  );
};
export default ConfirmBankPayment;
